import { DISCOUNT } from "$lib/config";
import { type ClassValue, clsx } from "clsx";
import currencyjs from "currency.js";
import { cubicOut } from "svelte/easing";
import type { TransitionConfig } from "svelte/transition";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type FlyAndScaleParams = {
  y?: number;
  x?: number;
  start?: number;
  duration?: number;
};

export const flyAndScale = (
  node: Element,
  params: FlyAndScaleParams = { y: -8, x: 0, start: 0.95, duration: 150 },
): TransitionConfig => {
  const style = getComputedStyle(node);
  const transform = style.transform === "none" ? "" : style.transform;

  const scaleConversion = (
    valueA: number,
    scaleA: [number, number],
    scaleB: [number, number],
  ) => {
    const [minA, maxA] = scaleA;
    const [minB, maxB] = scaleB;

    const percentage = (valueA - minA) / (maxA - minA);
    const valueB = percentage * (maxB - minB) + minB;

    return valueB;
  };

  const styleToString = (
    style: Record<string, number | string | undefined>,
  ): string => {
    return Object.keys(style).reduce((str, key) => {
      if (style[key] === undefined) return str;
      return str + `${key}:${style[key]};`;
    }, "");
  };

  return {
    duration: params.duration ?? 200,
    delay: 0,
    css: (t) => {
      const y = scaleConversion(t, [0, 1], [params.y ?? 5, 0]);
      const x = scaleConversion(t, [0, 1], [params.x ?? 0, 0]);
      const scale = scaleConversion(t, [0, 1], [params.start ?? 0.95, 1]);

      return styleToString({
        transform:
          `${transform} translate3d(${x}px, ${y}px, 0) scale(${scale})`,
        opacity: t,
      });
    },
    easing: cubicOut,
  };
};

export function format_currency(
  value: number,
  currency: string,
  up = false,
) {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });

  const new_value = up
    ? currencyjs(value).divide(1 - (DISCOUNT / 100)).value
    : value;

  return formatter.format(new_value);
}

export function isValidEmail(email: string | null | undefined) {
  if (!email) return false;
  return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(email);
}

export function format_date(value: string) {
  const date = new Date(value);

  const str = date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    minute: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
  });

  return str.replace(", ", " ");
}

export function format_date_blog(value: string) {
  const date = new Date(value);

  const str = date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return str.replace(", ", " ");
}

function sanitize_string(str: string) {
  return str.toLowerCase().replace(/\s/g, "");
}

export function search_items(
  items: any[],
  query: string | null,
) {
  if (!query?.trim()) return items;

  const results = items.filter((s: any) => {
    const name = sanitize_string(s.name);
    const q = sanitize_string(query);

    return name.includes(q);
  });

  return results;
}

type DateStyle = Intl.DateTimeFormatOptions["dateStyle"];
type TimeStyle = Intl.DateTimeFormatOptions["timeStyle"];

export function formatDate(
  date: Date | string,
  dateStyle: DateStyle = "medium",
  timeStyle?: TimeStyle,
  locales = "en",
) {
  const dateToFormat = typeof date == "string"
    ? new Date(date.replaceAll("-", "/"))
    : date;
  const dateFormatter = new Intl.DateTimeFormat(locales, {
    dateStyle,
    timeStyle,
  });
  return dateFormatter.format(dateToFormat);
}

export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function calculateReadingTime(text: string) {
  const wpm = 200;
  const text_length = text.split(" ").length;
  return Math.ceil(text_length / wpm);
}

export function formatPrice(
  amount: number,
  currency: string,
  decimals = 2,
) {
  const formatter = new Intl.NumberFormat("en-US", {
    currency,
    style: "currency",
    trailingZeroDisplay: "auto",
  });

  const value = amount / (10 ** decimals);

  return formatter.format(value);
}

export function valueToCurrency(
  amount: number | string,
  currency: string,
  quantity = 1,
) {
  const formatter = new Intl.NumberFormat("en-US", {
    currency,
    style: "currency",
    trailingZeroDisplay: "auto",
  });

  return formatter.format(currencyjs(amount).multiply(quantity).value);
}

export function priceToCurrency(
  prices: object,
  currency: string,
) {
  //@ts-ignore
  return valueToCurrency(prices[currency], currency);
}
